/**
 * Ajax Systems — EN 54 certified wireless FIRE line (2026-06-11).
 *
 * Added on request from Vetus Schola (ZA distributor). This is Ajax's
 * PROFESSIONAL fire-detection system (EN 54 certified, Jeweller radio,
 * launched 2024/25) — distinct from both the Ajax intrusion line in
 * ajaxAndHanwhaCatalog.ts and the residential FireProtect/FireProtect 2.
 *
 * Schema mapping follows the Cygnus fire precedent (cygnusFireCatalog.ts):
 *   - Fire Hub (CIE)        → 'alarm-panel'   (grade: 2 is an EN 50131
 *     placeholder — the real certs are EN 54-2/-4/-21/-25; tagline says so.
 *     Ajax even lists EN 50131 Grade 2 for the hub, so it is less of a
 *     stretch than for Cygnus.)
 *   - Fire ReX extender     → 'wireless-expander'
 *   - I/O Module (2X2)      → 'wireless-expander' (panel-side RF bus device)
 *   - Detectors (smoke/heat) → 'smoke-detector' with EN 54-5/-7 standards
 *   - Sounders / VADs       → 'indoor-sounder' (EN 54-3 / EN 54-23 in
 *     taglines; `acoustic` is a schema-required transducer field the
 *     datasheets don't publish — 'piezo' is the battery-sounder norm)
 *   - ManualCallPoint       → 'panic-button' (EN 54-11 fire call point —
 *     closest schema category; resettable frangible element = latch)
 *   - Internal Batteries    → 'battery' (hub/ReX backup cells)
 *   - GlandBox (125717)     → omitted: wiring accessory, not placeable.
 *
 * Every spec below was taken from the official ajax.systems product/spec
 * pages and adversarially re-verified against them on 2026-06-11 (incl.
 * distributor SKU anchoring for each part number the customer sent).
 * Where a figure is not published (e.g. MCP IP rating, EN 54-11 Type A/B)
 * it is omitted, not guessed.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const AJAX_FIRE_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Control & indicating equipment (CIE) + range extension
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ajax-en54-fire-hub-jeweller',
    manufacturer: 'Ajax',
    model: 'EN54 Fire Hub Jeweller (125732.315.WH)',
    category: 'alarm-panel',
    tagline:
      'EN 54-2/-4/-21/-25 wireless fire CIE, 10.1″ touchscreen, 200 devices / 40 fire zones, Jeweller 868 MHz up to 1.8 km, Ethernet + Wi-Fi + 2× LTE SIM, 24/72 h battery options',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fire-hub-jeweller/',
    specs: {
      series: 'Ajax EN54 Fire',
      zonesOnboard: 0,
      zonesMax: 40,
      partitions: 1,
      users: 200,
      // EN 50131 Grade 2 is genuinely listed by Ajax for this hub; fire
      // compliance (EN 54-2/-4/-21/-25) lives in the tagline.
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'Ajax PRO',
    },
  },
  {
    id: 'ajax-en54-fire-rex-jeweller',
    manufacturer: 'Ajax',
    model: 'EN54 Fire ReX Jeweller (135975.358.WH1)',
    category: 'wireless-expander',
    tagline:
      'EN 54-4/-18/-25 radio range extender for EN54 Fire Hub — 199 devices, up to 5 per system (≈35 km² coverage), events to CIE in 3 s, Ethernet primary + Jeweller, 50/100 h battery options',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fire-rex-jeweller/',
    specs: {
      frequency: '866.0–869.2 MHz (Jeweller, region-dependent)',
      biDirectional: true,
      zonesPerModule: 199,
      busConnection: 'Ethernet (primary) + Jeweller radio to CIE',
      compatibility: ['EN54 Fire Hub Jeweller'],
      rangeM: 1800,
    },
  },
  {
    id: 'ajax-en54-io-module-2x2-jeweller',
    manufacturer: 'Ajax',
    model: 'EN54 I/O Module (2X2) Jeweller (124590.280.WH1)',
    category: 'wireless-expander',
    tagline:
      'EN 54-18/-25 wireless I/O: 2 inputs (NO/NC/EOL/2EOL/3EOL — beam detectors, lift/HVAC panels) + 2 isolated relays (10–250 V~ up to 10 A / 10–30 V⎓ up to 5 A), 2× CR123A up to 5 yr',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-io-module-2x2-jeweller/',
    specs: {
      frequency: '866.0–869.2 MHz (Jeweller, region-dependent)',
      biDirectional: true,
      busConnection: 'Jeweller radio',
      compatibility: ['EN54 Fire Hub Jeweller'],
      rangeM: 1700,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // EN54 FireProtect detectors (white, ceiling, SmartBracket)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ajax-en54-fireprotect-smoke',
    manufacturer: 'Ajax',
    model: 'EN54 FireProtect (Smoke) Jeweller (119921.275.WH1)',
    category: 'smoke-detector',
    tagline:
      'EN 54-7 wireless dual-spectrum optical smoke detector, Jeweller 1.7 km, 2× CR123A up to 6 yr, IP20, ceiling SmartBracket',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fireprotect-smoke-jeweller/',
    specs: {
      detection: ['smoke-optical'],
      standard: ['EN 54-7'],
      standalone: false,
      batteryYears: 6,
      wirelessSystem: 'Ajax Jeweller (EN54 Fire)',
    },
  },
  {
    id: 'ajax-en54-fireprotect-smoke-sounder',
    manufacturer: 'Ajax',
    model: 'EN54 FireProtect (Smoke/Sounder) Jeweller (119919.276.WH1)',
    category: 'smoke-detector',
    tagline:
      'EN 54-7 optical smoke detector + built-in EN 54-3 sounder (65–93 dB, synced tones), 2× CR123A up to 5 yr, IP21C',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fireprotect-smoke-sounder-jeweller/',
    specs: {
      detection: ['smoke-optical'],
      standard: ['EN 54-7'],
      standalone: false,
      batteryYears: 5,
      wirelessSystem: 'Ajax Jeweller (EN54 Fire)',
    },
  },
  {
    id: 'ajax-en54-fireprotect-heat',
    manufacturer: 'Ajax',
    model: 'EN54 FireProtect (Heat) Jeweller (119917.273.WH1)',
    category: 'smoke-detector',
    tagline:
      'EN 54-5 wireless heat detector (classes A1/B/C, rate-of-rise + static: A1R…CS), dual thermistor, 2× CR123A up to 6 yr, IP20',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fireprotect-heat-jeweller/',
    specs: {
      detection: ['heat-fixed', 'heat-rate-of-rise'],
      standard: ['EN 54-5'],
      standalone: false,
      batteryYears: 6,
      wirelessSystem: 'Ajax Jeweller (EN54 Fire)',
    },
  },
  {
    id: 'ajax-en54-fireprotect-heat-sounder',
    manufacturer: 'Ajax',
    model: 'EN54 FireProtect (Heat/Sounder) Jeweller (119915.274.WH1)',
    category: 'smoke-detector',
    tagline:
      'EN 54-5 heat detector (A1R…CS) + built-in EN 54-3 sounder (66–89 dB, 32 synced tones), 2× CR123A up to 5 yr, IP21C',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fireprotect-heat-sounder-jeweller/',
    specs: {
      detection: ['heat-fixed', 'heat-rate-of-rise'],
      standard: ['EN 54-5'],
      standalone: false,
      batteryYears: 5,
      wirelessSystem: 'Ajax Jeweller (EN54 Fire)',
    },
  },
  {
    id: 'ajax-en54-fireprotect-heat-smoke',
    manufacturer: 'Ajax',
    model: 'EN54 FireProtect (Heat/Smoke) Jeweller',
    category: 'smoke-detector',
    tagline:
      'EN 54-5 + EN 54-7 multisensor (heat + smoke, combined-detection false-alarm filtering) — newest detector in the EN54 line',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/en54-fireprotect-jeweller-detectors/',
    specs: {
      detection: ['multi'],
      standard: ['EN 54-5', 'EN 54-7'],
      standalone: false,
      wirelessSystem: 'Ajax Jeweller (EN54 Fire)',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Sounders + visual alarm devices (red — as quoted by Vetus Schola;
  // white/black variants exist under neighbouring part numbers)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ajax-en54-fireprotect-sounder-red',
    manufacturer: 'Ajax',
    model: 'EN54 FireProtect (Sounder) Jeweller — Red (118604.278.RD1)',
    category: 'indoor-sounder',
    tagline:
      'EN 54-3/-25 wireless addressable fire sounder, up to 100 dB @ 1 m, 32 synced tones, 4× CR123A up to 5 yr, IP54, wall/ceiling',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fireprotect-sounder-jeweller/',
    specs: {
      // Transducer type isn't published by Ajax — 'piezo' is the norm for
      // battery fire sounders and satisfies the schema-required field.
      acoustic: 'piezo',
      optical: 'none',
      spLAt1mDb: 100,
      batteryBackup: 'built-in',
      ipRating: 'IP54',
    },
  },
  {
    id: 'ajax-en54-fireprotect-vad-red',
    manufacturer: 'Ajax',
    model: 'EN54 FireProtect (VAD) Jeweller — Red (118607.277.RD1)',
    category: 'indoor-sounder',
    tagline:
      'EN 54-23/-25 visual alarm device (no sounder) — red LED perimeter frame, 0.5 Hz temporal flash, ceiling ≤3 m / wall ≤2.4 m, 4× CR123A up to 5 yr, IP54',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fireprotect-vad-jeweller/',
    specs: {
      // Visual-only device — no sounder (EN 54-23 only).
      acoustic: 'none',
      optical: 'led-bar',
      batteryBackup: 'built-in',
      ipRating: 'IP54',
    },
  },
  {
    id: 'ajax-en54-fireprotect-sounder-vad-red',
    manufacturer: 'Ajax',
    model: 'EN54 FireProtect (Sounder/VAD) Jeweller — Red (118601.279.RD1)',
    category: 'indoor-sounder',
    tagline:
      'EN 54-3/-23/-25 combined sounder + VAD — up to 100 dB @ 1 m, 32 tones + red LED frame 0.5 Hz, 4× CR123A up to 5 yr, IP54',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-fireprotect-sounder-vad-jeweller/',
    specs: {
      acoustic: 'piezo',
      optical: 'led-bar',
      spLAt1mDb: 100,
      batteryBackup: 'built-in',
      ipRating: 'IP54',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Manual call point
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ajax-manualcallpoint-red-jeweller',
    manufacturer: 'Ajax',
    model: 'ManualCallPoint (Red) Jeweller (60815.171.NC1)',
    category: 'panic-button',
    tagline:
      'EN 54-11/-25 wireless manual fire call point — resettable frangible element + reset key, Jeweller 1.7 km, 2× CR123A up to 7 yr, indoor (only the red variant carries EN 54-25)',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/manualcallpoint-red-jeweller/',
    specs: {
      memory: 'mechanical-latch',
      connection: 'wireless',
      wirelessSystem: 'Jeweller',
      // Transparent protective front lid ships in the box (optional fit).
      accidentalPressProtection: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Hub / ReX backup batteries (the two "detector" codes from the
  // customer's email that turned out to be accessories)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ajax-en54-internal-battery-24h',
    manufacturer: 'Ajax',
    model: 'EN54 Internal Battery 24h (127876.316.WH)',
    category: 'battery',
    tagline:
      'Hot-swappable Li-ion backup for EN54 Fire Hub / Fire ReX — 24 h hub standby (50 h on ReX), EN 54-4 autonomy',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/en54-internal-battery/',
    specs: {
      voltage: 7.4,
      capacityAh: 5,
      chemistry: 'li-ion',
      use: 'alarm-backup',
    },
  },
  {
    id: 'ajax-en54-internal-battery-72h',
    manufacturer: 'Ajax',
    model: 'EN54 Internal Battery 72h (127878.324.WH)',
    category: 'battery',
    tagline:
      'Hot-swappable Li-ion backup for EN54 Fire Hub / Fire ReX — 72 h hub standby (100 h on ReX), EN 54-4 autonomy',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/en54-internal-battery-72/',
    specs: {
      voltage: 7.4,
      capacityAh: 10,
      chemistry: 'li-ion',
      use: 'alarm-backup',
    },
  },
];
